// Tabela curada pt-BR de países, hoje só para o NOME de exibição do país do
// aeroporto (`countryDisplayName`, planner de voo). Os campos de país do checkout
// leem o catálogo do perfil pelo `CountryField` (#3669); apagar esta tabela
// mudaria os nomes que o planner mostra em pt-BR e é decisão à parte.
// Fontes: ISO 3166-1 alpha-2.

import { resolveGenUiLocale } from '@voll-intelligence/genui-contract';

interface Country {
  code: string;
  name: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CO', name: 'Colômbia', flag: '🇨🇴' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguai', flag: '🇺🇾' },
  { code: 'PY', name: 'Paraguai', flag: '🇵🇾' },
  { code: 'BO', name: 'Bolívia', flag: '🇧🇴' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'EC', name: 'Equador', flag: '🇪🇨' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'ES', name: 'Espanha', flag: '🇪🇸' },
  { code: 'FR', name: 'França', flag: '🇫🇷' },
  { code: 'DE', name: 'Alemanha', flag: '🇩🇪' },
  { code: 'IT', name: 'Itália', flag: '🇮🇹' },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦' },
  { code: 'JP', name: 'Japão', flag: '🇯🇵' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'Índia', flag: '🇮🇳' },
  { code: 'AU', name: 'Austrália', flag: '🇦🇺' },
  { code: 'ZA', name: 'África do Sul', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigéria', flag: '🇳🇬' },
  { code: 'AO', name: 'Angola', flag: '🇦🇴' },
  { code: 'MZ', name: 'Moçambique', flag: '🇲🇿' },
];

// Mapa de código → country para lookup rápido O(1)
const COUNTRY_BY_CODE = new Map<string, Country>(COUNTRIES.map((c) => [c.code, c]));

/** `Intl.DisplayNames` de região no idioma, ou `null` quando o runtime não o tem. */
function regionNames(tag: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([tag], { type: 'region', fallback: 'none' });
  } catch {
    return null;
  }
}

/** O nome do país pra EXIBIR, pelo alpha-2, no idioma da sessão — `undefined` quando
 *  nem a tabela nem o ICU conhecem o código, e o chamador cai no que tiver. Em pt-BR a
 *  tabela curada vence, nos outros idiomas o ICU. Existe porque o fornecedor manda o nome ISO cru ("United States of America
 *  (the)", #4135), que não é nome de exibição em idioma nenhum. */
export function countryDisplayName(
  code: string,
  locale: string | null | undefined,
): string | undefined {
  const upper = code.trim().toUpperCase();
  if (!upper) return undefined;
  const tag = resolveGenUiLocale(locale);
  const curated = COUNTRY_BY_CODE.get(upper)?.name;
  if (tag === 'pt-BR' && curated) return curated;
  let icu: string | undefined;
  try {
    icu = regionNames(tag)?.of(upper);
  } catch {
    // Código fora do formato de região (RangeError do ICU): não há nome a dar.
    icu = undefined;
  }
  return icu ?? curated;
}
